/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { ContentStatus } from '@confluence/page-utils/entry-points/enums';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { errorContainsCodes, PERMISSION_ERROR_CODES } from '../detailsPanelUtils';
import { normalizeContentViewsData } from '../normalizers/normalizeContentViewsData';

import type {
	useContentViewsDetailsPanelContentViewsQuery as useContentViewsDetailsPanelContentViewsQuery$data,
	useContentViewsDetailsPanelContentViewsQueryVariables as useContentViewsDetailsPanelContentViewsQuery$variables,
} from './__types__/useContentViewsDetailsPanelContentViewsQuery';

const useContentViewsDetailsPanelContentViewsQuery = graphql`
	query useContentViewsDetailsPanelContentViewsQuery($contentId: ID!, $isDraft: Boolean!) {
		contentAnalyticsViewers(contentId: $contentId) @skip(if: $isDraft) {
			count
		}
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				permissionType
			}
		}
	}
`;
export const useContentViews = () => {
	const [contentId] = usePageContentId();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus();

	const { data, loading, error, refetch } = useQuery<
		useContentViewsDetailsPanelContentViewsQuery$data,
		useContentViewsDetailsPanelContentViewsQuery$variables
	>(useContentViewsDetailsPanelContentViewsQuery, {
		variables: {
			contentId: contentId ?? '',
			isDraft: contentStatus === ContentStatus.DRAFT,
		},
		skip: isContentStatusLoading || !!contentStatusError || !contentStatus || !contentId,
	});
	const isPermissionError = error && errorContainsCodes(error, PERMISSION_ERROR_CODES);

	const normalizedData = useMemo(() => normalizeContentViewsData(data), [data]);
	const { count, isInternalUser, isSSRError } = normalizedData;
	const contentViewsError = contentStatusError || error;

	// It should not be possible for the user to see "0 views", since the user is technically viewing the page
	// at that moment. However, due to the eventually consistent behavior of the analytics data, it is possible
	// that 0 comes back from the analytics service.
	const adjustedCount = count === 0 ? 1 : count;

	return {
		data: { ...normalizedData, count: adjustedCount },
		loading,
		error: contentViewsError,
		refetch: !!contentStatusError ? refetchContentStatus : refetch,
		shouldHideViewsAnalytics: Boolean(
			isPermissionError || !isInternalUser || isSSRError || contentViewsError || loading,
		),
	};
};
